import {Entities, StudentsPanel, SingleSelectState, StudentsPanelTabID} from '@esgi/main/features/teacher/students-panel';

import {ClassesList} from './components/classes-list';
import {GroupsList} from './components/groups-list';
import {Text} from '@esgi/ui/typography';
import {useEffect, useMemo, useState} from 'react';
import {storage, useStore} from '@esgi/main/libs/store';
import {styled} from '@esgi/ui/theme';
import {Box} from '@esgi/ui/layout';

type Props = {
	onSelect?: (value: SingleSelectState) => void;
};

const Panel = styled(Box, {
	minWidth: 272,
	width: 'max-content',
	overflow: 'hidden',
	height: '100%',
	padding: 20,
	backgroundColor: '$neutral94',
	backgroundImage: 'linear-gradient(332deg, #FAFAFA 17.26%, #F0F0F0 65.48%)',
});

export function ClassLevelStudentsPanel({onSelect}: Props) {
	const [selected, setSelected] = useState<SingleSelectState>({
		classId: null,
		groupId: null,
		studentId: null,
		studentFromClassId: null,
		studentFromGroupId: null,
	});

	const [classList, updateClassList, isClassListLoaded] = useStore(storage.classes);
	const [groupList, updateGroupList, isGroupListLoaded] = useStore(storage.groups);
	const [studentList, updateStudentList, isStudentListLoaded] = useStore(storage.students);

	const entities = useMemo<Entities>(
		() => ({
			classes: classList,
			groups: groupList,
			students: studentList,
		}),
		[classList, groupList, studentList],
	);

	useEffect(() => {
		if (selected) {
			onSelect?.(selected);
		}
	}, [selected]);

	const isPanelLoaded = isClassListLoaded && isGroupListLoaded && isStudentListLoaded;

	return (
		<Panel>
			<StudentsPanel.Root skeleton={!isPanelLoaded} entities={entities} initialTab={StudentsPanelTabID.Classes}>
				<StudentsPanel.Selection.Single selected={selected} onSelectedChange={setSelected}>
					<StudentsPanel.Header.Title>
						<Text size='small' color='neutral56'>
							Select a Class or a Group
						</Text>
					</StudentsPanel.Header.Title>
					<StudentsPanel.Header.Tabs />

					<StudentsPanel.Tabs.Root>
						<StudentsPanel.Tabs.Classes sectionTitle='Classes'>
							<ClassesList />
						</StudentsPanel.Tabs.Classes>
						<StudentsPanel.Tabs.Groups sectionTitle='Groups'>
							<GroupsList />
						</StudentsPanel.Tabs.Groups>
					</StudentsPanel.Tabs.Root>
				</StudentsPanel.Selection.Single>
			</StudentsPanel.Root>
		</Panel>
	);
}
